import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  Input,
  OnInit,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { AutoUnsubscribe, IconNames, MenuItemModel } from '../../../common';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { createDashboardMainMenuConfig } from '../constants/dashboard-main-menu-config';
import { Event, NavigationEnd, Router } from '@angular/router';
import { AppFacade } from '../../../store/state/app/app.facade';
import { AuthFacade } from '../../../store/state/auth/auth.facade';
import { SvgIconFacade } from '../../../store/state/svg-icon';
import { DOCUMENT } from '@angular/common';
import { HubContextFacade } from '../../../store/state/administration/hub/context/hub-context.facade';
import { HubBrief } from '../../../store/state/administration/hub/models/hub.model';
import { ValidationRulesService } from '../../../modular-validation/validation-service';
import { LocationFeatureFlags } from '../../../modular-validation/validation';

@Component({
  selector: 'dashboard-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@AutoUnsubscribe
export class NavBarComponent implements OnInit {
  @Input() sideNavDrawer: MatDrawer;
  @Input() activeMenuItem$: BehaviorSubject<string>;
  @Input() menuNavigation$: BehaviorSubject<string>;

  menuStructure: MenuItemModel[] = [];
  filteredMenuStructure$: Observable<MenuItemModel[]>;
  subscriptions: Subscription[] = [];
  subPanelNavigation$ = new BehaviorSubject<string>('');
  iconNameList = IconNames;
  svgPath = 'assets/core/icons/up.svg';

  constructor(
    public appFacade: AppFacade,
    public authFacade: AuthFacade,
    private router: Router,
    private cd: ChangeDetectorRef,
    private svgIconFacade: SvgIconFacade,
    @Inject(DOCUMENT) private document: Document,
    private hubContextFacade: HubContextFacade,
    private validationRulesService: ValidationRulesService,
  ) {}

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const isFullscreen = !!this.document.fullscreenElement;
    this.appFacade.setFullscreenMode(isFullscreen);
  }

  ngOnInit(): void {
    this.menuStructure = createDashboardMainMenuConfig();
    this.filteredMenuStructure$ = this.hubContextFacade.selectedHub$.pipe(
      switchMap((hub: HubBrief) => this.filterItems$(this.menuStructure, hub)),
    );
    this.subscriptions.push(
      this.activeMenuItem$.subscribe((title: string) => {
        if (title) {
          this.menuNavigation$.next(title);
          this.sideNavDrawer.open();
          this.cd.detectChanges();
        }
      }),
      this.router.events.subscribe((val: Event) => {
        if (val instanceof NavigationEnd && this.sideNavDrawer) {
          this.sideNavDrawer.close();
        }
      }),
    );

    if (!this.svgIconFacade.getSvgIconMap().has(this.svgPath)) {
      this.svgIconFacade.loadSvgIcon(this.svgPath);
    }
  }

  private filterItems$(items: MenuItemModel[], hub: HubBrief): Observable<MenuItemModel[]> {
    if (!items.length) {
      return of([]);
    }
    const checks$ = items.map((item: MenuItemModel) => this.isItemVisible$(item, hub));
    return combineLatest(checks$).pipe(
      switchMap((visible: boolean[]) => {
        const visibleItems = items.filter((_item: MenuItemModel, i: number) => visible[i]);
        const withFilteredChildren$ = visibleItems.map((item: MenuItemModel) => {
          if (!item.children?.length) {
            return of(item);
          }
          return this.filterItems$(item.children, hub).pipe(
            map((filteredChildren: MenuItemModel[]) => ({ ...item, children: filteredChildren })),
          );
        });
        return withFilteredChildren$.length ? combineLatest(withFilteredChildren$) : of([]);
      }),
    );
  }

  private isItemVisible$(item: MenuItemModel, hub: HubBrief): Observable<boolean> {
    const featureFlagOk =
      !item.featureFlagKey ||
      this.validationRulesService.getFeatureFlag(
        hub?.country,
        hub?.id,
        item.featureFlagKey as keyof LocationFeatureFlags,
      );
    const hubPropertyOk = !item.hubPropertyKey || !!hub?.[item.hubPropertyKey];
    const allFlagsOk = featureFlagOk && hubPropertyOk;

    if (!item.requiredRole) {
      return of(allFlagsOk);
    }
    return this.authFacade.hasRole$(item.requiredRole).pipe(map((hasRole: boolean) => hasRole && allFlagsOk));
  }
}
